import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "../common/modals/Modal";
import BigModal from '../common/modals/BigModal';
import SumUp from "./SumUp";
import TimePicker from "./TimePicker";
import { isEmailFormatCorrect } from '../../utils/formUtils';
import { timeToMinutes, minutesToTime } from "../../utils/timeUtils";
import configApp from '../../config';
import useFetchGraphQL from '../../hooks/useFetchGraphQL';
import useFetchBookings from '../../hooks/useFetchBookings';
import FloatingLabelInput from '../common/inputs/FloatingLabelInput';
import FloatingLabelInputGroup from '../common/inputs/FloatingLabelInputGroup';
import FormMessage from '../common/inputs/FormMessage';
import PaymentTypeSelector from '../common/selectors/PaymentTypeSelector';
import Captcha from "../common/inputs/Captcha";
import { useTranslation } from 'react-i18next';

const BookingModal = ({ t, booking, isAvailable, initialModalStep="initial", onClose, origin, startTimeForm, timeSlots }) => {
  const [modalStep, setModalStep] = useState(initialModalStep); // "initial", created, "payment", "paid", "payment_voucher", "paid_voucher_result", "cabin_error", "cancellation", "cancelled", "error"
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(false);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [createdBooking, setCreatedBooking] = useState({});
  const [confirmedBooking, setConfirmedBooking] = useState({});
  const [startTime, setStartTime] = useState(startTimeForm);
  const [endTime, setEndTime] = useState("");
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [startTimeSlotsForDesk, setStartTimeSlotsForDesk] = useState([]);
  const [endTimeSlotsForDesk, setEndTimeSlotsForDesk] = useState([]);
  const [ratesDB, setRatesDB] = useState([]);
  const { fetchRates, fetchSpots } = useFetchGraphQL();
  const { createBooking, confirmBooking, cancelBooking, manageCabinBooking } = useFetchBookings();
  const logo = <div className="big-modal-main"><img src={configApp.globalLogo} alt="Logo"></img></div>
  const isCabin = booking?.ResourceId?.startsWith("C");
  const [bookingCode, setBookingCode] = useState("");
  const [paymentType, setPaymentType] = useState('card');
  const [voucherId, setVoucherId] = useState("");
  const [paidVoucherResult, setPaidVoucherResult] = useState("");
  const [cabinError, setCabinError] = useState("");
  const [captchaSuccess, setCaptchaSuccess] = useState(false);
  const { i18n } = useTranslation();

  // Load rates
  useEffect(() => {
    const loadRates = async () => {
      try {
        const rates = await fetchRates();
        setRatesDB(rates);
      } catch (error) {
        console.error(error);
      }
    };
    loadRates();
  }, []);

  // Load available slots (init)
  useEffect(() => {
    if (modalStep === "initial") {
      setLoadingSlots(true);
      fetchSpots(booking.StartDate, booking.ResourceId)
      .then((response) => { 
        const unavailableSlotsForDesk = JSON.parse(JSON.stringify(response.data.data.spots.items)).map(slot => slot.Time);
        const availableStartSlots = availableStartSlotsForResource(timeSlots, unavailableSlotsForDesk, isCabin ? configApp.globalTimeCabinSlot : configApp.globalMinimumTimeSlot);
        setStartTimeSlotsForDesk(availableStartSlots);
        if (startTime) {
          setEndTimeSlotsForDesk(availableEndSlotsForResource(startTime, availableStartSlots, timeSlots, configApp.globalMinimumTimeSlot, isCabin));
        }
      })
      .catch((error) => { 
        console.error(error);
      })
      .finally(() => {
        setLoadingSlots(false);
      });
    }
  }, []);

  // Update endTimeSlotsForDesk and endTime when startTime changes
  useEffect(() => {
    if (modalStep === "initial") {
      const endTimeSlotsForDesk = availableEndSlotsForResource(startTime, startTimeSlotsForDesk, timeSlots, configApp.globalMinimumTimeSlot, isCabin)
      setEndTimeSlotsForDesk(endTimeSlotsForDesk);
      setEndTime(endTimeSlotsForDesk[0])
    }
  }, [startTime]);

  /* FUNCTIONS     ---------------------------------------------- */
  
  const navigate = useNavigate();

  const handleClose = async () => {
    let refresh = true;
    if (modalStep === 'initial' || (modalStep === 'created' && !loading)) {
      setStartTimeSlotsForDesk([]);
      setEndTimeSlotsForDesk([]);
      refresh = false;
    } else if (modalStep === 'payment' || (modalStep === 'created' && loading) || modalStep === 'payment_voucher') {
      if(!window.confirm(t("pages.booking_new.modal.stop_payment"))) { return }
      handleCancellation(false); // automatic cancellation
    }
    onClose(refresh);
  }

  const handleConfirmationOrCancellation = async (modalStep) => {
    setLoading(true);
    modalStep === 'paid' ? await handlePayment() : await handleCancellation(true); // option 2 = cancellation done by user
    setLoading(false);
  };

  const handleOverviewButtonClick = () => {
    navigate('/bookings#booking-overview-form');
    onClose();
  };

  const handleNewButtonClick = () => {
    navigate('/bookings');
    onClose();
  }

  /* INITIAL MODAL ---------------------------------------------- */

  const handleContinue = () => {
    setEmailError(false);
    if (!isEmailFormatCorrect(email)) {
      setEmailError(true);
      return;
    }
    booking.EndTime = endTime;
    isCabin ? booking.BookingCode = bookingCode : booking.Name = name;
    booking.Email = email;
    booking.AmountPaid = calculateRate(startTime, endTime, ratesDB);
    setModalStep("created");
  }

  const initialBody = (
    isCabin ?
    /* if CABIN <------------------------------------ */
    <div className="modal-main">
      {isAvailable ? 
        <h2>{t("pages.booking_new.modal.text_available")}</h2> : 
        <h2>{t("pages.booking_new.modal.text_unavailable").replace("%startTime%", startTimeForm)}</h2>
      }

      <div className="modal-item-container">
        <div className="input-container">
            <FloatingLabelInput label={t("pages.booking_new.modal.date")} value={booking ? booking.StartDate : ''} disabled />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', justifyContent: 'center', padding: '0 0 0 10px' }}>
          {/* Start Time Row */}
          <p className="modal-item" style={{ margin: 0 }}>
            {t("pages.booking_new.modal.start_time_short", { returnObjects: true })} :
          </p>
          <TimePicker t={t} selectedTime={startTime} setSelectedTime={setStartTime} timeOptions={startTimeSlotsForDesk} loading={loadingSlots} /> 
          {/* End Time Row */}
          <p className="modal-item" style={{ margin: 0 }}>
            {t("pages.booking_new.modal.end_time_short", { returnObjects: true })} :
          </p>
          <TimePicker t={t} selectedTime={endTime} setSelectedTime={setEndTime} timeOptions={endTimeSlotsForDesk} loading={loadingSlots} dependOn={true}/>
        </div>

        <FormMessage message={t("pages.booking_new.modal.form_message_info_cabin").replace("%globalMaximunTimeCabinSlot%", configApp.globalMaximunTimeCabinSlot)} type={"info"} />

        <div className="input-container">
          <FloatingLabelInput label={t("pages.booking_new.modal.email")} value={email} onChange={(e) => setEmail(e.target.value)} error={emailError} disabled={!loadingSlots && startTimeSlotsForDesk.length === 0} />
          <FloatingLabelInput label={t("pages.booking_new.modal.booking_code")} value={bookingCode} onChange={(e) => setBookingCode(e.target.value)} disabled={!loadingSlots && startTimeSlotsForDesk.length === 0} />
          <FloatingLabelInput label={t("pages.booking_new.modal.amount_paid")} value={t("pages.booking_new.modal.cabin_included")} disabled />
        </div>
      </div>
    </div>

    : /* if DESK <------------------------------------- */
    <div className="modal-main">
      {isAvailable ? 
        <h2>{t("pages.booking_new.modal.text_available")}</h2> : 
        <h2>{t("pages.booking_new.modal.text_unavailable").replace("%startTime%", startTimeForm)}</h2>
      }
      <div className="modal-item-container">
        <div className="input-container">
            <FloatingLabelInput label={t("pages.booking_new.modal.date")} value={booking ? booking.StartDate : ''} disabled />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', alignItems: 'center', justifyContent: 'center', padding: '0 0 0 10px' }}>
          {/* Start Time Row */}
          <p className="modal-item" style={{ margin: 0 }}>
            {t("pages.booking_new.modal.start_time_short", { returnObjects: true })} :
          </p>
          <TimePicker t={t} selectedTime={startTime} setSelectedTime={setStartTime} timeOptions={startTimeSlotsForDesk} loading={loadingSlots}/>
          {/* End Time Row */}
          <p className="modal-item" style={{ margin: 0 }}>
            {t("pages.booking_new.modal.end_time_short", { returnObjects: true })} :
          </p>
          <TimePicker t={t} selectedTime={endTime} setSelectedTime={setEndTime} timeOptions={endTimeSlotsForDesk} loading={loadingSlots} dependOn={true}/>
        </div>

        <FormMessage message={t("pages.booking_new.modal.form_message_info").replace("%globalMinimumTimeSlot%", configApp.globalMinimumTimeSlot)} type={"info"} />

        <div className="input-container">
          <FloatingLabelInput label={t("pages.booking_new.modal.email")} value={email} onChange={(e) => setEmail(e.target.value)} error={emailError} disabled={!loadingSlots && startTimeSlotsForDesk.length === 0} />
          <FloatingLabelInput label={t("pages.booking_new.modal.name")} value={name} onChange={(e) => setName(e.target.value)} disabled={!loadingSlots && startTimeSlotsForDesk.length === 0} />
          <FloatingLabelInput label={t("pages.booking_new.modal.amount_paid")} value={startTime && endTime ? calculateRate(startTime, endTime, ratesDB) + ' €' : t("pages.booking_new.modal.amount_paid_condition")} disabled italic={!endTime} />
        </div>
      </div>
    </div>
  );

  const disabledConditions = isCabin ? !(startTime && endTime && email && bookingCode) : !(startTime && endTime && email && name);
  const initialButton = (
    <>
      <button onClick={handleClose} className="modal-button close-modal-button">{t("pages.booking_new.modal.close_button")}</button>
      <button onClick={handleContinue} className="modal-button continue-modal-button" disabled={disabledConditions}>
        {t("pages.booking_new.modal.continue_button")}
      </button>
    </>
  );

  /* CREATED MODAL ---------------------------------------------- */

  const handleCreation = async () => {
    if (isCabin) {
      try {
        setLoading(true);
        const data = await manageCabinBooking(booking.ResourceId, email, bookingCode, booking.StartDate, booking.EndDate, startTime, endTime, "1"); // "1" to confirm 
        if (!data.isSuccess) { 
          setModalStep("error");
        } else {
          setConfirmedBooking(data.booking);
          setModalStep('paid');
        }
      } catch (error) { 
        if ([404, 409].includes(error.response?.status)) { 
          setCabinError(String(error.response.status));
          setModalStep("cabin_error");
        } else {
          setModalStep("error");
        }
      } finally {
        setLoading(false);
      }
      return;
    }
    
    try {
      setLoading(true);
      const data = await createBooking(email, name, startTime, endTime, booking, calculateRate(startTime, endTime, ratesDB), i18n.language); 
      if (!data.isSuccess) { 
        setModalStep("error");
      } else {
        setCreatedBooking(data.booking)
        setModalStep(paymentType === 'voucher' ? "payment_voucher" : "payment");
      }
    } catch (error) { 
      setModalStep("error");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentTypeChange = () => {
    setPaymentType(paymentType === 'card' ? 'voucher' : 'card');
  }

  const handlePaymentByVoucher = async () => {
    setLoading(true);
    const sumUpData = { paymentResult: {} };
    try { 
      const data = await confirmBooking(createdBooking, sumUpData, voucherId);
      setConfirmedBooking(data.booking)
      setModalStep("paid");
    } catch (error) { 
      if ([404, 409].includes(error.response?.status)) { 
        setPaidVoucherResult(String(error.response.status));
        setModalStep("paid_voucher_result");
      } else {
        setModalStep("error");
      }
    } finally {
      setLoading(false);
    }
  };

  const createdBody = (
    <div className="big-modal-main">
      <h2>{t("pages.booking_new.modal.text_ongoing_booking")}</h2>
      <div className="input-container">
        <FloatingLabelInputGroup 
          inputs={[
            { label: t("pages.booking_new.modal.date"), value: modalStep !== "created" ? createdBooking.startDate : booking.StartDate, disabled: true },
            { label: t("pages.booking_new.modal.start_time"), value: modalStep !== "created" ? createdBooking.startTime : startTime, disabled: true },
          ]}
        />
        <FloatingLabelInputGroup 
          inputs={[
            { label: t("pages.booking_new.modal.end_date"), value: modalStep !== "created" ? createdBooking.endDate : booking.EndDate, disabled: true },
            { label: t("pages.booking_new.modal.end_time"), value: modalStep !== "created" ? createdBooking.endTime : endTime, disabled: true },
          ]}
        />
        <FloatingLabelInput label={t("pages.booking_new.modal.email")} value={modalStep !== "created" ? createdBooking.email : email} disabled />
        {isCabin ? 
          <FloatingLabelInput label={t("pages.booking_new.modal.booking_code")} value={modalStep !== "created" ? createdBooking.bookingCode : bookingCode} disabled />
          :
          <FloatingLabelInput label={t("pages.booking_new.modal.name")} value={modalStep !== "created" ? createdBooking.name : name} disabled />
        }
        {isCabin ? 
          <FloatingLabelInput label={t("pages.booking_new.modal.amount_paid")} value={t("pages.booking_new.modal.cabin_included")} disabled />
          :
          <FloatingLabelInput label={t("pages.booking_new.modal.amount_paid")} value={paymentType === 'card' ? (modalStep !== "created" ? createdBooking.amountPaid + ' €' : calculateRate(startTime, endTime, ratesDB) + ' €') : "0 €"} disabled />
        }
      </div>
      {modalStep === 'created' && <Captcha setCaptchaSuccess={setCaptchaSuccess}/>}
      {!isCabin && 
        <PaymentTypeSelector
          mode={t("pages.booking_new.modal.payment_mode")}
          cardText={t("pages.booking_new.modal.payment_card")}
          voucherText={t("pages.booking_new.modal.payment_voucher")}
          selectedType={paymentType} 
          onChange={handlePaymentTypeChange}
          disabled={modalStep !== 'created'}
        />
      }
      {modalStep === "payment_voucher" &&
         <div className="input-container">
           <FloatingLabelInput label={t("pages.booking_new.modal.voucher")} value={voucherId} onChange={(e) => setVoucherId(e.target.value)} disabled={loading || modalStep !== "payment_voucher"}/>
         </div>
       }
    </div>
  );

  const createdButton = (
    <>
      {!loading &&
        <>
          <button onClick={() => setModalStep('initial')} className="modal-button close-modal-button">{t("pages.booking_new.modal.back_button")}</button>
          <button onClick={() => handleCreation()} className="modal-button continue-modal-button" disabled={!captchaSuccess}>{t("pages.booking_new.modal.confirm_button")}</button>
        </>
      }
      {loading && <div className="loading-spinner"><span>{t("pages.booking_new.modal.loading")}</span></div>}
    </>
  );

  const paymentVoucherButton = (
    <>
      {!loading && modalStep === "payment_voucher" &&
        <button onClick={() => handlePaymentByVoucher()} className="modal-button continue-modal-button" disabled={loading || !voucherId}>{t("pages.booking_new.modal.pay_button")}</button>
      }
      {loading && <div className="loading-spinner"><span>{t("pages.booking_new.modal.paying")}</span></div>}
    </>
  ); 

  /* PAYMENT MODAL ---------------------------------------------- */
  
  const handlePayment = async (body) => {
    const sumUpData = { paymentResult: { "body": body } };
    try { 
      const data = await confirmBooking(createdBooking, sumUpData);
      if (!data.isSuccess) { 
        setModalStep("error");
      } else {
        setConfirmedBooking(data.booking);
        setModalStep("paid");
      }
    } catch (error) { 
      setModalStep("error");
    }
  };

  const paymentBody = (
    <div className="big-modal-main">  
      <div className="sumup-container">
        <SumUp bookingCheckoutId={createdBooking.checkoutId} email={createBooking.Email} handlePayment={handlePayment} setModalStep={setModalStep}/>
      </div>
    </div>
  );

  const paymentButton = (
    <>
      <button onClick={handleClose} className="modal-button close-modal-button">{t("pages.booking_new.modal.close_button")}</button>
    </>
  );

  /* PAID    MODAL ---------------------------------------------- */

  const paidBody = (
    <div className="big-modal-main">
      <h2>{t("pages.booking_new.modal.title_paid")}</h2>
      <div className="input-container">
        <FloatingLabelInputGroup 
          inputs={[
            { label: t("pages.booking_new.modal.date"), value: confirmedBooking.startDate, disabled: true },
            { label: t("pages.booking_new.modal.start_time"), value: confirmedBooking.startTime, disabled: true },
          ]}
        />
        <FloatingLabelInputGroup 
          inputs={[
            { label: t("pages.booking_new.modal.end_date"), value: confirmedBooking.endDate, disabled: true },
            { label: t("pages.booking_new.modal.end_time"), value: confirmedBooking.endTime, disabled: true },
          ]}
        />
        <FloatingLabelInput label={t("pages.booking_new.modal.email")} value={confirmedBooking.email} disabled />
        <FloatingLabelInput label={t("pages.booking_new.modal.name")} value={confirmedBooking.name} disabled />
        <FloatingLabelInput label={t("pages.booking_new.modal.amount_paid")} value={confirmedBooking.amountPaid || confirmedBooking.amountPaid === 0 ? confirmedBooking.amountPaid + ' €' : ''} disabled />
        <FloatingLabelInputGroup 
          inputs={[
            { label: t("pages.booking_new.modal.desk"), value: confirmedBooking.resourceId, disabled: true },
            { label: t("pages.booking_new.modal.booking_code"), value: confirmedBooking.bookingCode, disabled: true },
          ]}
        />
        <FloatingLabelInput label={t("pages.booking_new.modal.state")} value={getState(t, confirmedBooking.status)} disabled />

        {confirmedBooking.extras && (() => {
          let extras = [];
          try { extras = confirmedBooking.extras } 
          catch (e) { return null }

          if (!Array.isArray(extras) || extras.length === 0) { return null }
          
          return (
            <>
              <div style={{ marginTop: "5px" }}> {t("pages.booking_new.modal.extra")} : </div>
      
              {extras.map((extra, index) => (
                <FloatingLabelInputGroup
                  key={index}
                  inputs={[
                    { label: t("pages.booking_new.modal.cabin"), value: `${extra.resourceId} 🕒 ${extra.startTime} - ${extra.endTime}`, disabled: true },
                  ]}
                />
              ))}
            </>
          );
        })()}
      </div>
    </div>
  );

  const paidButton = (
    <>
      <button onClick={() => handleOverviewButtonClick()} className="modal-button close-modal-button">{t("pages.booking_new.modal.overview_button")}</button>
      <button onClick={() => handleNewButtonClick()} className="modal-button continue-modal-button">{t("pages.booking_new.modal.new_button")}</button>
    </>
  );

    /* PAID VOUCHER RESULT MODAL ----------------------------------- */

    const paidVoucherResultBody = (
      <div className="big-modal-main">
        <h2>
          {paidVoucherResult === "404" && t("pages.booking_new.modal.voucher_404")}
          {paidVoucherResult === "409" && t("pages.booking_new.modal.voucher_409")}
        </h2>
        <div className="input-container">
            {paidVoucherResult === "404" && t("pages.booking_new.modal.voucher_404_text")}
            {paidVoucherResult === "409" && t("pages.booking_new.modal.voucher_409_text")}
          <div style={{marginTop: "20px"}}>
            <button onClick={() => setModalStep('payment_voucher')} className="modal-button close-modal-button" disabled={loading}>{t("pages.booking_new.modal.back_button")}</button>
          </div>
        </div>
      </div>
    )
  
    const paidVoucherButton = (
      <button onClick={onClose} className="modal-button close-modal-button">{t("pages.booking_new.modal.close_button")}</button>
    );

        /* CABIN ERROR MODAL ----------------------------------- */

        const cabinErrorBody = (
          <div className="big-modal-main">
            <h2>
              {cabinError === "404" && t("pages.booking_new.modal.cabin_404")}
              {cabinError === "409" && t("pages.booking_new.modal.cabin_409")}
            </h2>
            <div className="input-container">
                {cabinError === "404" && t("pages.booking_new.modal.cabin_404_text")}
                {cabinError === "409" && t("pages.booking_new.modal.cabin_409_text")}
              <div style={{marginTop: "20px"}}>
                <button onClick={() => setModalStep('initial')} className="modal-button close-modal-button" disabled={loading}>{t("pages.booking_new.modal.back_button")}</button>
              </div>
            </div>
          </div>
        )
      
        const cabinErrorButton = (
          <button onClick={onClose} className="modal-button close-modal-button">{t("pages.booking_new.modal.close_button")}</button>
        );

    /* CANCELN MODAL ---------------------------------------------- */

    const handleCancellation = async (byUser) => {
      const bookingId = origin === 'new' ? `${createdBooking.bookingCode}_${createdBooking.email}` : `${booking.BookingCode}_${booking.Email}`;
  
      // if not by user = automatic cancellation then close modal
      try {
        const data = await cancelBooking(bookingId);
        byUser ? setModalStep(data.isSuccess ? "cancelled" : "error") : onClose();
      } catch (error) { 
        byUser ? setModalStep("error") : onClose();
      }
    };

  const cancellationBody = (
    <div className="big-modal-main"> 
      <h2>{t("pages.booking_new.modal.title_cancellation", { returnObjects: true })}</h2>
      <div className="">
        <p className="modal-item"><strong>{t("pages.booking_new.modal.text_cancellation", { returnObjects: true })}</strong></p>
        <div style={{marginTop: "20px"}}>
          {!loading && modalStep !== "cancelled" &&
            <button onClick={() => handleConfirmationOrCancellation('cancelled')} className="modal-button cancel-modal-button" disabled={loading}>{t("pages.booking_new.modal.cancel_button")}</button>
          }
          {loading && <div className="loading-spinner"><span>{t("pages.booking_new.modal.cancellation")}</span></div>}
        </div>
      </div>
    </div>
  );

  const cancellationButton = (
    <>
      <button onClick={onClose} className="modal-button close-modal-button">{t("pages.booking_new.modal.close_button")}</button>
    </>
  );

  /* CANCELD MODAL ---------------------------------------------- */

  const cancelledBody = (
    <div className="big-modal-main"> 
      <h2>{t("pages.booking_new.modal.title_cancelled", { returnObjects: true })}</h2>
      <div className="input-container">
        {t("pages.booking_new.modal.text_cancelled", { returnObjects: true })}
      </div>
      <img src="/images/result/check-box_12503639.png" alt="Success" width="50px" height="50px" style={{ marginBottom: "10px"}}/>
    </div>
  );

  const cancelledButton = (
    <>
      <button onClick={() => handleOverviewButtonClick()} className="modal-button close-modal-button">{t("pages.booking_new.modal.overview_button")}</button>
      <button onClick={() => handleNewButtonClick()} className="modal-button continue-modal-button">{t("pages.booking_new.modal.new_button")}</button>
    </>
  );

  /* ERROR   MODAL ---------------------------------------------- */

  const errorBody = (
    <div className="big-modal-main"> 
      <h2>{t("pages.booking_new.modal.title_error")}</h2>
      <div className="input-container">
        {t("pages.booking_new.modal.text_error")}
      </div>
      <img src="/images/result/vote-no_12503624.png" alt="Error" width="50px" height="50px" style={{ marginBottom: "10px"}}/>
    </div>
  );

  const errorButton = (<button onClick={onClose} className="modal-button close-modal-button">{t("pages.booking_new.modal.close_button")}</button>);

  /* RETURN        ---------------------------------------------- */

  return (
    <>
      {["initial"].includes(modalStep) && (
        <Modal
          title={(isCabin ? t("pages.booking_new.modal.cabin") : t("pages.booking_new.modal.desk")) + ' ' + (booking && booking.ResourceId ? booking.ResourceId : '-')}
          onClose= {handleClose} 
          body={{ initial: initialBody }[modalStep]}
          footerButton={{ initial: initialButton }[modalStep]}
        />
      )}
      {["created", "payment", "paid", "payment_voucher", "paid_voucher_result", "cabin_error", "cancellation", "cancelled", "error"].includes(modalStep) && (
        <BigModal
          title={booking && booking.ResourceId ? (isCabin ? t("pages.booking_new.modal.cabin") : t("pages.booking_new.modal.desk")) + ' ' + booking.ResourceId : ''}
          leftContent={{ created: createdBody, payment: createdBody, payment_voucher: createdBody, paid: paidBody, paid_voucher_result: paidVoucherResultBody, cabin_error: cabinErrorBody, cancellation: cancellationBody, cancelled: cancelledBody, error: errorBody }[modalStep]}
          rightContent={{ created: logo, payment: paymentBody, paid: logo, payment_voucher: logo, paid_voucher_result: logo, cabin_error: logo, cancellation: logo, cancelled: logo, error: logo }[modalStep]}
          footerButton={{ initial: initialButton, created: createdButton, payment: paymentButton, paid: paidButton, payment_voucher: paymentVoucherButton, paid_voucher_result: paidVoucherButton, cabin_error: cabinErrorButton, cancellation: cancellationButton, cancelled: cancelledButton, error: errorButton }[modalStep]}
          onClose={handleClose}
          onBack={modalStep === 'created' && !loading ? () => setModalStep('initial') : undefined}
        />
      )}
    </>
  );
};

export default BookingModal;

const getState = (t, state) => {
  if (state) {
    const states = t("pages.bookings.states", { returnObjects: true });
    const foundState = Object.values(states).find((s) => s.options.includes(state));
    return foundState ? foundState.text : t("pages.bookings.states.unknown.text");
  }
  return ""
}

const availableStartSlotsForResource = (startSlots, unavailableSlots, minTimeSlot) => {
  if (startSlots.length === 0) { return [] }
  
  return startSlots.filter(slot => {
    const slotMinutes = timeToMinutes(slot);


    // Cabin
    if (minTimeSlot < 1) {
      const slotEnd = slotMinutes + minTimeSlot * 60;

      for (let i = 0; i < unavailableSlots.length; i++) {
        const unavStart = timeToMinutes(unavailableSlots[i]);
        const unavEnd = unavStart + minTimeSlot * 60;

        if (slotMinutes < unavEnd && slotEnd > unavStart) {
          return false;
        }
      }

      if (slotMinutes === timeToMinutes(configApp.globalEndHour + ":00")) {
        return false;
      }

      return true;
    }

    for (let i = 0; i < unavailableSlots.length; i++) {
      const unavailableTime = unavailableSlots[i];
      const unavailableMinutes = timeToMinutes(unavailableTime);

      if (slotMinutes >= unavailableMinutes - minTimeSlot * 60 + 30 && slotMinutes < unavailableMinutes) {
        return false;
      }

      if (slotMinutes <= unavailableMinutes + minTimeSlot * 60 - 30 && slotMinutes > unavailableMinutes) {
        return false;
      }
    }

    if(timeToMinutes(slot) === timeToMinutes(configApp.globalEndHour + ":00")) {
      return false;
    }

    return true; 
  });
};

const availableEndSlotsForResource = (startTime, availableStartSlotsForResource, timeSlots, minTimeSlot, isCabin) => {
  if (isCabin) {
    if (availableStartSlotsForResource.length === 0) { return [] }
    
    const cabinSlots = [];
    const maxSlots = configApp.globalMaximunTimeCabinSlot * 2; // globalMaximunTimeCabinSlot in h => 2 times 30 minutes 
    const interval = configApp.globalTimeCabinSlot * 60;
    let currentTime = timeToMinutes(startTime); //Math.max(timeToMinutes(startTime), timeToMinutes(availableStartSlotsForResource[0]));
    const lastAvailableSlotMinutes = timeToMinutes(timeSlots[timeSlots.length - 1]);

    for (let i = 0; i < maxSlots; i++) {
      currentTime += interval;
      if (currentTime > lastAvailableSlotMinutes) { break }
      if (availableStartSlotsForResource.includes(minutesToTime(currentTime-interval))) { 
        cabinSlots.push(minutesToTime(currentTime));
      } else {
        break;
      }
    }
    return cabinSlots;
  }
  
  const filteredTimeSlots = timeSlots.filter(endSlot => {
    const endMinutes = timeToMinutes(endSlot);
    const startTimeMinutes = timeToMinutes(startTime);

    if (endMinutes < startTimeMinutes + (minTimeSlot * 60)) {
      return false;
    }

    const validStartSlot = availableStartSlotsForResource.some(startSlot => {
      const startMinutes = timeToMinutes(startSlot);
      return endMinutes === (startMinutes + (minTimeSlot * 60));
    });

    if (!validStartSlot) {
      return false;
    }

    if (startTimeMinutes % 60 !== endMinutes % 60) {
      return false;
    }

    return true;
  });

  if (availableStartSlotsForResource.length !== 0 && filteredTimeSlots.length === 0) {
    filteredTimeSlots.push(configApp.globalEndHour + ":00");
  }

  return filteredTimeSlots;
};

const calculateRate = (startTime, endTime, ratesDB) => {
  if (!startTime || !endTime || ratesDB.length === 0) return '';

  const durationInHours = (timeToMinutes(endTime) - timeToMinutes(startTime)) / 60;
  const hdayRate = ratesDB.find(rate => rate.id === "hday");
  const hourRate = ratesDB.find(rate => rate.id === "hour");
  const dayRate = ratesDB.find(rate => rate.id === "day");

  if (durationInHours <= 4) {
    return hdayRate ? hdayRate.TotalAmount : '';
  }

  if (durationInHours <= 7) {
    const extraHours = durationInHours - 4;
    return hdayRate && hourRate ? hdayRate.TotalAmount + (hourRate.TotalAmount * extraHours) : '';
  }

  return dayRate ? dayRate.TotalAmount : '';
};